import React, {
  HTMLAttributes,
  HTMLProps,
  useMemo,
  useState,
} from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import {
  Paragraph,
} from '@uncinc/react-drupal-core';

import {
  Container,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import { DEFAULT_LANGUAGE, ROUTE_TYPE_SELF_HELP } from '../../../config/constants';
import { Button, CoreLink, Main } from '../../../components';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import { ImmutableGenericNodeEntity } from '..';

import './style.scss';

interface Props extends HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableGenericNodeEntity;
}

const NodeOnboarding: React.FC<Props> = ({ entity, className }) => {
  const history = useHistory();
  const { state, search } = useLocation();
  const { t } = useTranslation();
  const { buildClass, joinClasses } = useClasses('NodeOnboarding');
  const {
    onboarding: {
      credentials,
      credentialsHti,
      checklistHti,
    },
    login,
    landing,
  } = useRoutes(ROUTE_TYPE_SELF_HELP);

  const [countQuestions, setCountQuestions] = useState<number>(1);

  const langcode = entity.getIn(['langcode', 0, 'value'], DEFAULT_LANGUAGE);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const path = entity.getIn(['paths', langcode], '');
  const title = entity.getIn(['title', 0, 'value'], '');
  const body = entity.getIn(['body', 0, 'processed'], '');
  const paragraphs = entity.get('field_paragraphs', Immutable.List());

  const allQuestionsAnswered = countQuestions > paragraphs.size;
  const [isInvalidSubmitAttempted, setIsInvalidSubmitAttempted] = React.useState<boolean>(false);

  const destination = useMemo(() => {
    // New way of using HTI (updating user).
    if (path === checklistHti) {
      return credentialsHti;
    }

    // Old way of using HTI (register user).
    const params = new URLSearchParams(search);
    // Pass on the hti param.
    if (params.get('hti') !== null) {
      return `${credentials}${search}`;
    }
    return credentials;
  }, [checklistHti, credentials, credentialsHti, path, search]);

  return (
    <Container>
      <div className="row">
        <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 py-4 py-md-12">
          <article typeof="schema:WebPage" className={joinClasses(className, buildClass())}>
            <header className={buildClass('header')}>
              <CoreLink
                href={landing}
                title={t('buttons.back')}
                className="icon-before icon-chevron icon-orientation-left"
                onClick={() => {
                  if (state === login) {
                    history.goBack();
                  }
                }}
              />
            </header>

            <Main className="content">
              <div className={buildClass('title')}>
                <h1>{title}</h1>
              </div>

              {body.length > 0 && (
                <WysiwygField
                  className={buildClass('body')}
                  property="schema:text"
                  html={body}
                />
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();

                  if (!allQuestionsAnswered) {
                    setIsInvalidSubmitAttempted(true);
                    return;
                  }

                  history.push(destination);
                }}
              >
                {paragraphs.size > 0 && (
                  <div className={buildClass('paragraphs')}>
                    {paragraphs.map((paragraph, index) => {
                      const id = paragraph.get('target_id');
                      if (index < countQuestions) {
                        return (
                          <Paragraph
                            key={`paragraph:${id}`}
                            id={id}
                            entity={paragraph.get('entity')}
                            setCountQuestions={setCountQuestions}
                            countQuestions={countQuestions}
                            instanceOfParagraph={index + 1}
                            isInvalidSubmitAttempted={isInvalidSubmitAttempted}
                          />
                        );
                      }
                      return null;
                    })}
                  </div>
                )}

                <Button
                  type="submit"
                  className={buildClass('submit')}
                >
                  {t('buttons.next')}
                </Button>
              </form>

            </Main>
          </article>
        </div>
      </div>
    </Container>
  );
};

export default NodeOnboarding;
